// Processes the offline edit queue against live server state.
//
// For each queued entry we do a fresh server read and compare:
//   • If the server's updatedAt still matches our baseline, no one else edited
//     while we were offline → commit our patch and drop the entry.
//   • If the server changed but its current value already equals our patch, our
//     edit is a no-op → just drop the entry.
//   • Otherwise it's a real conflict → AUTO-MERGE via the line-aware utility
//     in `lib/mergeConflict.js` and write the merged result. The "keep mine /
//     theirs / both" modal is gone — Walter 2026-05-04 found it confusing,
//     and "keep both" creating a duplicate list was bad UX. The merge prepends
//     offline-only lines to the top of server content with a marker comment
//     so the in-list banner can surface "conflicting edits at top — review &
//     dismiss" to the affected list.

import {
  doc, collection, setDoc, getDocFromServer, serverTimestamp,
  writeBatch, increment,
} from 'firebase/firestore';
import { db } from './firebase';
import { loadQueue, removeFromQueue } from './offlineQueue';
import { mergeConflict, mergeTitle } from './mergeConflict';
import { mergeThreeWay } from './threeWayMerge';
import { getClientId } from './clientId';

export async function processOfflineQueue(userId) {
  const queue = loadQueue();
  const entries = Object.values(queue);
  if (entries.length === 0) return { autoCommitted: 0, autoMerged: 0 };

  let autoCommitted = 0;
  let autoMerged = 0;

  for (const entry of entries) {
    const { listId, boardId, patch, baselineUpdatedAtMs, baselineContent } = entry;
    const ref = doc(db, 'users', userId, 'boards', boardId, 'lists', listId);

    let snap;
    try {
      snap = await getDocFromServer(ref);
    } catch (err) {
      // Transient network failure — leave the entry for the next online event.
      console.warn('[conflict] server read failed for', listId, err);
      continue;
    }

    if (!snap.exists()) {
      // List was deleted on another device. Our offline patch is moot.
      removeFromQueue(listId);
      continue;
    }

    const server = snap.data();
    const serverUpdatedAtMs = server.updatedAt?.toMillis?.() ?? 0;
    const baselineMatches =
      baselineUpdatedAtMs != null && serverUpdatedAtMs === baselineUpdatedAtMs;

    // Walter 2026-06-22 — CONTENT is the authoritative "did anyone else touch
    // it?" signal. If the server's content is still exactly what our offline
    // edit was based on, no other device changed it, so our edit applies
    // cleanly — REGARDLESS of the timestamp. This fixes the false single-
    // device conflict: a brief offline blip (no second device) used to flag a
    // conflict because the timestamp baseline didn't line up, even though the
    // server content was untouched. (The old `fieldsActuallyDiffer` check
    // compared server-vs-OUR-PATCH, which is always "different" since we just
    // added a line — the wrong comparison. Server-vs-BASELINE is the right one.)
    const contentUnchangedOnServer =
      baselineContent != null && 'content' in patch
      && (server.content ?? '') === baselineContent;

    // Do the fields we touched actually differ from what's on the server?
    const fieldsActuallyDiffer = Object.entries(patch).some(
      ([k, v]) => (server[k] ?? '') !== (v ?? '')
    );

    if (contentUnchangedOnServer || baselineMatches || !fieldsActuallyDiffer) {
      // Clean commit — no merge needed.
      // F5 fix (Walter 2026-05-24): stamp lastWriteByClientId on the
      // clean-commit path the same way the trivial-merge + substantive-
      // merge branches below already do. Without this stamp, a later
      // online conflict-detection write on another device sees a moved
      // `updatedAt` with no attribution → the `isSelfRecentWrite` guard
      // in historyConflict.js misses → phantom conflict gets flagged.
      // All three branches in this function now stamp consistently.
      await setDoc(ref, {
        ...patch,
        updatedAt: serverTimestamp(),
        lastWriteByClientId: getClientId(),
      }, { merge: true });
      removeFromQueue(listId);
      autoCommitted += 1;
      continue;
    }

    // The server genuinely diverged from our offline baseline. Merge each
    // prose field. Walter 2026-06-24: for CONTENT, prefer a true 3-way merge
    // (we have the base = `baselineContent`, theirs = server.content, mine =
    // patch.content). When the two sides' edits don't overlap — one device
    // added/edited lines the other left alone, or one deleted a line the other
    // kept — the merge is CLEAN and we apply it SILENTLY (no band, no history
    // snapshot). Only a genuine same-line clash (clean === false) falls back to
    // the snapshot-to-history + conflictPendingReview review flow. This is what
    // makes the dormant-device wake-up resolve invisibly in the common case.
    // Legacy queue entries with no captured base fall back to the older 2-way
    // line merge (mergeConflict), whose hadConflict drives the same review flow.
    const mergedPatch = {};
    let writeMarker = false; // true → genuine clash needing review
    for (const [k, v] of Object.entries(patch)) {
      const serverValue = server[k] ?? '';
      if (k === 'content') {
        if (baselineContent != null) {
          const { merged, clean } = mergeThreeWay(baselineContent, v, serverValue);
          mergedPatch.content = merged;
          if (!clean) writeMarker = true;
        } else {
          const { merged, hadConflict } = mergeConflict(serverValue, v);
          mergedPatch.content = merged;
          if (hadConflict) writeMarker = true;
        }
      } else if (k === 'title') {
        mergedPatch.title = mergeTitle(serverValue, v);
      } else {
        // Non-prose field — last-write-wins is fine. (Position, z,
        // state, etc. — these have no semantic merge.)
        mergedPatch[k] = v;
      }
    }
    // Walter 2026-05-17: substantive conflict path. Snapshot the
    // cloud's pre-merge content+title to the list's /history
    // subcollection, increment historyCount, and set
    // `conflictPendingReview` so the band knows to surface. The
    // mergedPatch already contains the OFFLINE content as-is (no
    // prepend-marker mangling) — the side-by-side history modal
    // makes the diff visible without rewriting the live list.
    // Only reached when the merge was NOT clean (a real same-line clash).
    if (writeMarker) {
      const histColRef = collection(
        db, 'users', userId, 'boards', boardId, 'lists', listId, 'history',
      );
      const histRef = doc(histColRef);
      const batch = writeBatch(db);
      batch.set(histRef, {
        content: server.content ?? '',
        title: server.title ?? '',
        prevUpdatedAt: server.updatedAt ?? null,
        replacedAt: serverTimestamp(),
        replacedByUid: userId,
        replacedByClientId: getClientId(),
        reason: 'offline-merge',
        // Walter 2026-06-22: the 3-way base (content we went offline from) so
        // the conflict modal can offer "merge both (beta)". Null for pre-this
        // queued edits that never captured a content baseline.
        baseContent: baselineContent ?? null,
      });
      batch.set(
        ref,
        {
          ...mergedPatch,
          updatedAt: serverTimestamp(),
          historyCount: increment(1),
          // Drives the band + history icon. Cleared when the user
          // resolves via the modal.
          conflictPendingReview: serverTimestamp(),
          lastWriteByClientId: getClientId(),
        },
        { merge: true },
      );
      await batch.commit();
    } else {
      // Clean merge — the two sides' edits didn't overlap, so the union is
      // safe to write silently. No band: this is the common dormant-device
      // path and interrupting it for every wake-up would be noise.
      //
      // Walter 2026-09-03 (content-writer audit): it now leaves a SNAPSHOT,
      // though. "Clean" means the algorithm found no overlapping edits, not
      // that the result is correct — `threeWayMerge` reads a moved block as a
      // delete plus an insert (#168), so a both-sides reorder can misjudge,
      // and a misjudged CLEAN merge dropped lines with no band and nothing in
      // history to recover from. He lost most of a paragraph that way. One
      // small write, only on an actual merge, and the pre-merge cloud text is
      // always recoverable.
      // Only when a merge ACTUALLY happened. If the server still matches the
      // baseline nobody else touched it, so this is just our own queued edit
      // landing — there is no third version to lose and a snapshot would be
      // one wasted history write per offline replay. (Caught by
      // conflictResolver.test's "server content untouched since baseline"
      // case, which is exactly this scenario.)
      const serverDiverged = baselineContent != null
        && (server.content ?? '') !== baselineContent;
      const preMergeChanged = serverDiverged
        && (server.content ?? '') !== (mergedPatch.content ?? server.content ?? '');
      if (preMergeChanged) {
        const histColRef = collection(
          db, 'users', userId, 'boards', boardId, 'lists', listId, 'history',
        );
        const histRef = doc(histColRef);
        const batch = writeBatch(db);
        batch.set(histRef, {
          content: server.content ?? '',
          title: server.title ?? '',
          prevUpdatedAt: server.updatedAt ?? null,
          replacedAt: serverTimestamp(),
          replacedByUid: userId,
          replacedByClientId: getClientId(),
          reason: 'pre-merge',
          baseContent: baselineContent ?? null,
          mergeSource: 'offline-replay',
        });
        batch.set(ref, {
          ...mergedPatch,
          updatedAt: serverTimestamp(),
          historyCount: increment(1),
          lastWriteByClientId: getClientId(),
        }, { merge: true });
        await batch.commit();
      } else {
        await setDoc(ref, {
          ...mergedPatch,
          updatedAt: serverTimestamp(),
          lastWriteByClientId: getClientId(),
        }, { merge: true });
      }
    }
    removeFromQueue(listId);
    if (writeMarker) autoMerged += 1;
    else autoCommitted += 1;
  }

  return { autoCommitted, autoMerged };
}
